import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, RefreshCw, Users, Database, AlertTriangle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface SyncResults {
  success: boolean;
  customersProcessed: number;
  subscriptionsCreated: number;
  subscriptionsUpdated: number;
  errors: string[];
  missedCustomers: any[];
  timestamp: string;
}

export function StripeWebhookMonitor() {
  const { isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncResults, setLastSyncResults] = useState<SyncResults | null>(null);

  if (!isAdmin) return null;

  const runSync = async () => {
    setIsLoading(true);
    toast({
      title: "Syncing with Stripe",
      description: "Checking for missed customers and updating subscription data...",
    });

    try {
      const { data, error } = await supabase.functions.invoke('stripe-sync-customers');
      
      if (error) {
        throw error;
      }

      setLastSyncResults(data);
      
      if (data.success) {
        toast({
          title: "Sync Completed Successfully",
          description: `Processed ${data.customersProcessed} customers. Created ${data.subscriptionsCreated} new subscriptions, updated ${data.subscriptionsUpdated}.`,
          variant: "default",
        });
      } else {
        toast({
          title: "Sync Completed with Issues",
          description: `Some errors occurred during sync. Check the details below.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "An error occurred during sync",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testWebhook = async () => {
    toast({
      title: "Testing Webhook",
      description: "Sending test event to verify webhook is working...",
    });

    try {
      const { data, error } = await supabase.functions.invoke('stripe-webhook', {
        body: { verify: true }
      });
      
      if (error) {
        throw error;
      }

      if (data.stripe_key_configured && data.webhook_secret_configured) {
        toast({
          title: "Webhook Test Successful",
          description: "Your webhook configuration is working correctly.",
          variant: "default",
        });
      } else {
        toast({
          title: "Webhook Configuration Issues",
          description: "Some configuration issues detected. Check your secrets.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Webhook Test Failed",
        description: error.message || "Failed to test webhook",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Stripe Data Synchronization
          </CardTitle>
          <CardDescription>
            Ensure no customers are missed by synchronizing your Stripe data with your subscription database
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={runSync} 
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Syncing...' : 'Sync Stripe Data'}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={testWebhook}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Test Webhook
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => window.open("https://dashboard.stripe.com/webhooks", "_blank")}
            >
              Open Stripe Dashboard
            </Button>
          </div>

          {lastSyncResults && (
            <div className="mt-6 space-y-4">
              <h4 className="font-semibold">Last Sync Results</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{lastSyncResults.customersProcessed}</div>
                  <div className="text-sm text-blue-800">Customers Processed</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{lastSyncResults.subscriptionsCreated}</div>
                  <div className="text-sm text-green-800">New Subscriptions</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{lastSyncResults.subscriptionsUpdated}</div>
                  <div className="text-sm text-orange-800">Updated Subscriptions</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{lastSyncResults.errors.length}</div>
                  <div className="text-sm text-red-800">Errors</div>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                Last sync: {new Date(lastSyncResults.timestamp).toLocaleString()}
              </div>

              {lastSyncResults.errors.length > 0 && (
                <div className="mt-4">
                  <h5 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Errors ({lastSyncResults.errors.length})
                  </h5>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {lastSyncResults.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {lastSyncResults.missedCustomers.length > 0 && (
                <div className="mt-4">
                  <h5 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Missed Customers ({lastSyncResults.missedCustomers.length})
                  </h5>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {lastSyncResults.missedCustomers.map((customer, index) => (
                      <div key={index} className="text-sm bg-orange-50 p-2 rounded">
                        <div className="font-medium">{customer.email}</div>
                        <div className="text-orange-700">Reason: {customer.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Data Integrity Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Webhook endpoint configured in Stripe</span>
              <Badge variant="outline">Manual Check Required</Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Webhook secret matches environment variable</span>
              <Badge variant="outline">Test Webhook Above</Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">All Stripe customers synced to database</span>
              <Badge variant="outline">Run Sync Above</Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">Payment totals match Stripe records</span>
              <Badge variant="outline">Verified During Sync</Badge>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h5 className="font-medium text-blue-800 mb-2">Recommended Actions</h5>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Run sync daily to catch any missed webhook events</li>
              <li>• Monitor webhook logs in Stripe dashboard for failures</li>
              <li>• Test webhook after any configuration changes</li>
              <li>• Check that webhook endpoint URL is correct in Stripe</li>
              <li>• Ensure webhook events include: checkout.session.completed, invoice.paid</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}